// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback } from 'react';

import type { MachineElement, BendData } from '@evolvemep/api-ts';

import { convertInchesToImperialString } from 'helpers/convertInchesToImperialString';
import { isNotNil } from 'helpers/isNotNil';
import { toHighlightableObjectName } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/HighlightableGroup';
import { useBendPreviewContext } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useBendPreviewContext';

import { BendDataCard } from './BendDataCard';
import { BendDataOutput } from './BendDataOutput';

export type BendResultState = { isSuccess: true; data: BendData } | { isSuccess: false; message: string };

type Props = {
  element: MachineElement;
  bendResult: BendResultState;
  showAllMarks: boolean;
};

/** Shows the layout and final dimensions for a bend. */
export const BendResults = ({ element, bendResult, showAllMarks }: Props) => {
  const { highlightedObject, setHighlightedObject } = useBendPreviewContext();

  // Helper method to get event props for `BendDataOutputs` on elements that can be highlighted in the preview
  const getHighlightableSectionProps = useCallback(
    (label: string) => {
      const labelObjectName = toHighlightableObjectName(label);
      return {
        highlighted: highlightedObject === labelObjectName,
        cardSectionProps: {
          onMouseEnter: () => setHighlightedObject(labelObjectName),
          onMouseLeave: () => setHighlightedObject(null),
        },
      };
    },
    [highlightedObject, setHighlightedObject],
  );

  if (!bendResult.isSuccess) {
    return (
      <BendDataCard>
        <BendDataOutput label="Bend is invalid for this profile" value={bendResult.message} textProps={{ c: 'red' }} />
      </BendDataCard>
    );
  }

  return (
    <>
      {bendResult.data.finalDimensions.length > 0 && (
        <BendDataCard title="Final Dimensions">
          {bendResult.data.finalDimensions.map(({ label, value, isAngle }) => (
            <BendDataOutput
              key={label}
              label={label}
              value={isAngle ? `${value.toLocaleString()}°` : convertInchesToImperialString(value * 12)}
              {...getHighlightableSectionProps(label)}
            />
          ))}
        </BendDataCard>
      )}
      <BendDataCard title="Layout Dimensions">
        {isNotNil(bendResult.data.springBackAngle) && (
          <BendDataOutput
            label="Springback Angle"
            value={`${bendResult.data.springBackAngle.toLocaleString()}°`}
            tooltip="Bend to this angle so the finished bend lands on its design angle after the material springs back, based on the springback settings defined in the bender profile."
          />
        )}
        {(showAllMarks ? bendResult.data.layoutDimensions : []).map(({ label, value }) => (
          <BendDataOutput
            key={label}
            label={label}
            value={convertInchesToImperialString(value * 12)}
            {...getHighlightableSectionProps(label)}
          />
        ))}
        <BendDataOutput
          key="Overall Length"
          label="Overall Length"
          tooltip="This field shows the total length of the conduit before bending and adjusted according to the elongation settings defined in the bender profile."
          value={convertInchesToImperialString((element.adjustedLength ?? element.length) * 12)}
          {...getHighlightableSectionProps('Overall Length')}
        />
      </BendDataCard>
    </>
  );
};
